/**
 * Review Queue screen — item 11 (Asana 1216373781992284), spec §5.8 / §10.3 / §11.
 *
 * Settles a contradiction between the disposition spec and this codebase's own v3 dev handoff:
 * §5.8/§10.3 say consultant confirmation is always bulk ("review AI recommendations, approve/
 * override in batch, then commit"); LogicDecomposerDashboard.tsx's header comment (still there,
 * unedited until this item) says row checkboxes / Approve / bulk selection were REMOVED because
 * "LD is a read/analyze surface, not a work-assignment tool." Ryan's call (2026-07-30 Asana
 * comment): "The spec wins — that §3 note predates the six-outcome framework, and 'AI recommends,
 * human confirms' doesn't close without a commit step." This screen is that commit step.
 *
 * Filter -> bulk select -> approve/override -> Commit, one request per decision (POST /api/
 * framework/commit takes ONE `decision` for the whole selected batch — "approve this batch" and
 * "override this batch to box4, here's why" are the two batch-level actions the spec describes,
 * not N one-off writes stitched together client-side). Routing is computed and persisted
 * server-side (commitRouting.js); the preview below mirrors that table for display ONLY — the
 * committed `routing` shown on a row afterward always comes back from the server response.
 *
 * Defaults to showing only the needs-review queue (status human_review | provisional — the same
 * two statuses MigrationArchitectWorkspace.tsx's header chip already counts, spec §10.3's 0.60-
 * 0.85 confidence band + ties + gate overturns, plus unresolved Cat 3) since that is what "the
 * needs-review queue behind the header chip" (item 11's own wording) means — a toggle switches to
 * the full committable population for bulk-approving already-confident records too.
 */

import { useMemo, useState } from 'react';
import type { DispositionRecord, Outcome, RecordStatus, RoutingStep, Timing } from './types';
import { useFrameworkData } from './useFrameworkData';
import { color, font } from '../tokens';

const C = {
  accent: color.accent, deep: color.navy, ink: '#1e1b3a',
  body: '#4a4966', subtle: color.textMuted, label: '#7c7a95', border: color.cardBorder, soft: '#f9fafb', surface: color.surface,
};
const mono = 'JetBrains Mono, monospace';

const OUTCOME_LABEL: Record<Outcome, string> = {
  box1: 'Box 1 — Full modernization', box2: 'Box 2 — Governed / light', box3: 'Box 3 — Platform migration',
  box4: 'Box 4 — Document & retain', consolidate: 'Consolidate', decommission: 'Decommission', queue: 'Queue (no disposition yet)',
};
const OUTCOME_SHORT: Record<Outcome, string> = {
  box1: 'Box 1', box2: 'Box 2', box3: 'Box 3', box4: 'Box 4', consolidate: 'Consolidate', decommission: 'Decommission', queue: 'Queue',
};
const OUTCOME_ORDER: Outcome[] = ['box1', 'box2', 'box3', 'box4', 'decommission', 'consolidate', 'queue'];
// Overridable to any real outcome except 'queue' — there is no "route to queue" action, an EUC
// only sits in queue until the engine or a consultant gives it a real disposition.
const OVERRIDE_TARGETS: Outcome[] = ['box1', 'box2', 'box3', 'box4', 'decommission', 'consolidate'];
const TIMING_LABEL: Record<Timing, string> = { cat1: 'Cat 1 · Immediate', cat2: 'Cat 2 · Near-term', cat3: 'Cat 3 · Deferred' };
const STATUS_STYLE: Record<RecordStatus, { bg: string; fg: string; label: string }> = {
  auto_confirmed: { bg: '#e9f9ef', fg: '#166534', label: 'Auto-confirmed' },
  committed: { bg: '#ede9fe', fg: C.accent, label: 'Committed' },
  human_review: { bg: '#fef3c7', fg: '#9a5b00', label: 'Human review' },
  provisional: { bg: '#fee2e2', fg: '#991b1b', label: 'Provisional' },
  deferred: { bg: '#fee2e2', fg: '#991b1b', label: 'Deferred' },
  regenerate: { bg: '#f1f0f6', fg: C.label, label: 'Regenerate' },
};
// spec §5.8's own confidence routing thresholds — display only, the backend is what actually gated
// these into a status (dispositionEngine.js's routeByConfidence).
const CONF_LEVEL = (c: number | null): 'high' | 'medium' | 'low' | 'none' =>
  c == null ? 'none' : c > 0.85 ? 'high' : c >= 0.6 ? 'medium' : 'low';
const CONF_LABEL: Record<'high' | 'medium' | 'low' | 'none', string> = {
  high: 'High (>0.85)', medium: 'Medium (0.60–0.85)', low: 'Low (<0.60)', none: 'No score',
};
// Approve-as-is requires one of these two statuses (mirrors backend framework.js's
// COMMITTABLE_STATUSES — kept in sync manually since it's a tiny, stable set; see module doc).
const APPROVABLE: Set<RecordStatus> = new Set(['auto_confirmed', 'human_review']);
const PATH_LABEL: Record<string, string> = { A: 'Path A', B: 'Path B', C: 'Path C' };

// UI-only mirror of backend/src/services/logicDecomposer/framework/commitRouting.js's spec §11
// table, for the preview panel before a commit is sent. Never used to decide what actually gets
// committed — that is always the server's response after POST /api/framework/commit.
function previewRoute(disposition: Outcome, box3IncludePathC: boolean): RoutingStep[] {
  switch (disposition) {
    case 'box1': return [{ path: 'A', profile: 'full', note: 'Full SDLC rebuild' }];
    case 'box2': return [{ path: 'A', profile: 'light', note: 'Governed / light-SDLC modernization' }];
    case 'box3': {
      const r: RoutingStep[] = [{ path: 'A', profile: 'platform_config', note: 'Platform configuration requirements' }];
      if (box3IncludePathC) r.push({ path: 'C', profile: 'cloud_decision', note: 'Platform-vs-on-prem placement needs justifying' });
      r.push({ path: 'B', profile: 'sunset', note: 'Legacy EUC sunset once the migration lands' });
      return r;
    }
    case 'box4': return [{ path: null, profile: 'doc_bundle', note: 'Standalone documentation bundle — no generation chain' }];
    case 'decommission': return [{ path: 'B', profile: 'sunset', note: 'Sunset, comms, compliance evidence' }];
    case 'consolidate': return [{ path: 'B', profile: 'sunset', note: 'Consolidation loser — folded into its survivor' }];
    default: return [];
  }
}
function routeSummary(steps: RoutingStep[] | null | undefined): string {
  if (!steps || !steps.length) return '—';
  return steps.map((s) => (s.path ? `${PATH_LABEL[s.path]} (${s.profile})` : s.note)).join(' + ');
}

interface Props {
  onToast?: (message: string) => void;
}

export default function ReviewQueue({ onToast }: Props) {
  const live = useFrameworkData();
  const records = live.records;

  const [outcome, setOutcome] = useState<'all' | Outcome>('all');
  const [timing, setTiming] = useState<'all' | Timing>('all');
  const [confLevel, setConfLevel] = useState<'all' | 'high' | 'medium' | 'low' | 'none'>('all');
  const [sourceType, setSourceType] = useState('all');
  const [dept, setDept] = useState('all');
  const [gate, setGate] = useState('all');
  const [needsReviewOnly, setNeedsReviewOnly] = useState(true);
  const [showCommitted, setShowCommitted] = useState(false);
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [decisionMode, setDecisionMode] = useState<'approve' | 'override' | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<Outcome>('box1');
  const [reason, setReason] = useState('');
  const [box3IncludePathC, setBox3IncludePathC] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [lastResult, setLastResult] = useState<{ committedIds: string[]; errors: { euc_id: string; error: string }[] } | null>(null);

  const sourceTypes = useMemo(() => Array.from(new Set(records.map((r) => r.source_type).filter(Boolean))).sort(), [records]);
  const depts = useMemo(() => Array.from(new Set(records.map((r) => r.dept).filter(Boolean))).sort(), [records]);
  const gates = useMemo(() => {
    const s = new Set<string>();
    for (const r of records) for (const g of r.gates_fired || []) s.add(g.id);
    return Array.from(s).sort();
  }, [records]);

  const filtered = useMemo(() => records.filter((r) => {
    if (outcome !== 'all' && r.disposition !== outcome) return false;
    if (timing !== 'all' && r.timing !== timing) return false;
    if (confLevel !== 'all' && CONF_LEVEL(r.confidence) !== confLevel) return false;
    if (sourceType !== 'all' && r.source_type !== sourceType) return false;
    if (dept !== 'all' && r.dept !== dept) return false;
    if (gate !== 'all' && !(r.gates_fired || []).some((g) => g.id === gate)) return false;
    if (needsReviewOnly && !(r.status === 'human_review' || r.status === 'provisional')) return false;
    if (!showCommitted && r.committed) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!r.euc_id.toLowerCase().includes(q) && !r.file.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [records, outcome, timing, confLevel, sourceType, dept, gate, needsReviewOnly, showCommitted, search]);

  const selectedRecords = useMemo(() => filtered.filter((r) => selected.has(r.euc_id)), [filtered, selected]);
  const approvableCount = selectedRecords.filter((r) => APPROVABLE.has(r.status)).length;
  const needsOverrideCount = selectedRecords.length - approvableCount;

  // What each selected row will actually be routed as, for the preview + the box3-Path-C toggle:
  // its override target when overriding, otherwise its own current disposition when approving.
  const targetDispositions = useMemo(() => {
    if (decisionMode === 'override') return selectedRecords.length ? [overrideTarget] : [];
    return Array.from(new Set(selectedRecords.map((r) => r.disposition).filter((d): d is Outcome => d !== 'queue')));
  }, [decisionMode, overrideTarget, selectedRecords]);
  const showBox3Toggle = targetDispositions.includes('box3');

  const previewGroups = useMemo(() => {
    const groups = new Map<string, { disposition: Outcome; count: number; steps: RoutingStep[] }>();
    for (const r of selectedRecords) {
      const d = decisionMode === 'override' ? overrideTarget : r.disposition;
      if (d === 'queue') continue;
      const key = d;
      if (!groups.has(key)) groups.set(key, { disposition: d, count: 0, steps: previewRoute(d, box3IncludePathC) });
      groups.get(key)!.count += 1;
    }
    return Array.from(groups.values());
  }, [selectedRecords, decisionMode, overrideTarget, box3IncludePathC]);

  const toggleRow = (eucId: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(eucId)) next.delete(eucId); else next.add(eucId);
    return next;
  });
  const selectAllFiltered = () => setSelected(new Set(filtered.map((r) => r.euc_id)));
  const clearSelection = () => { setSelected(new Set()); setDecisionMode(null); setReason(''); setLastResult(null); };

  const canSubmit = decisionMode === 'approve'
    ? selectedRecords.length > 0
    : decisionMode === 'override'
      ? selectedRecords.length > 0 && reason.trim().length >= 10
      : false;

  const submit = async () => {
    if (!decisionMode || !canSubmit) return;
    setCommitting(true);
    setLastResult(null);
    const res = await live.commit({
      eucIds: selectedRecords.map((r) => r.euc_id),
      decision: decisionMode,
      overrideDisposition: decisionMode === 'override' ? overrideTarget : undefined,
      reason: decisionMode === 'override' ? reason.trim() : undefined,
      box3IncludePathC,
    });
    setCommitting(false);
    if (!res.ok || !res.result) {
      onToast?.(res.error || 'Commit failed');
      return;
    }
    const { committed, errors, committed_count, error_count } = res.result;
    setLastResult({ committedIds: committed.map((c) => c.euc_id), errors });
    onToast?.(
      error_count === 0
        ? `Committed ${committed_count} EUC${committed_count === 1 ? '' : 's'}.`
        : `Committed ${committed_count}, ${error_count} failed — see below.`,
    );
    // Drop succeeded rows from the selection; keep failed ones selected so the consultant can see
    // and retry them (e.g. fix the reason, or approve instead of override) without reselecting.
    setSelected(new Set(errors.map((e) => e.euc_id)));
    if (error_count === 0) { setDecisionMode(null); setReason(''); }
  };

  const filterSelect = (label: string, value: string, onChange: (v: string) => void, options: { value: string; label: string }[]) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: C.subtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{
        fontFamily: font.ui, fontSize: 12, color: C.ink, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', fontWeight: 600,
      }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );

  if (!live.loading && records.length === 0) {
    return (
      <div style={{ fontFamily: font.ui, color: C.body, maxWidth: 560, margin: '48px auto', textAlign: 'center', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '40px 32px' }}>
        <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>Review Queue</div>
        <h2 style={{ margin: '10px 0 6px', fontSize: 18, fontWeight: 800, color: C.ink }}>No inventory loaded yet</h2>
        <p style={{ fontSize: 13.5, color: C.subtle, margin: 0 }}>Load an inventory from the Framework Mapping tab first — there is nothing to review until EUCs have dispositions.</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: font.ui, color: C.body }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: font.ui, fontSize: 11, letterSpacing: '0.12em', color: C.label, textTransform: 'uppercase' }}>Review Queue</div>
        <h1 style={{ margin: '4px 0 4px', fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: '-0.02em' }}>
          Filter, bulk approve or override, then commit.
        </h1>
        <p style={{ margin: 0, fontSize: 12.5, color: C.subtle }}>
          Approving or committing routes each EUC to its downstream generation chain (spec §11). Overrides require a reason — every one is an audit trail entry.
        </p>
      </div>

      {/* Filters — spec §10.3: disposition box, timing band, confidence level, source type,
          department, gates fired, needs-review. */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        {filterSelect('Disposition', outcome, (v) => setOutcome(v as any), [{ value: 'all', label: 'All' }, ...OUTCOME_ORDER.map((o) => ({ value: o, label: OUTCOME_SHORT[o] }))])}
        {filterSelect('Timing', timing, (v) => setTiming(v as any), [{ value: 'all', label: 'All' }, { value: 'cat1', label: 'Cat 1' }, { value: 'cat2', label: 'Cat 2' }, { value: 'cat3', label: 'Cat 3' }])}
        {filterSelect('Confidence', confLevel, (v) => setConfLevel(v as any), [{ value: 'all', label: 'All' }, { value: 'high', label: CONF_LABEL.high }, { value: 'medium', label: CONF_LABEL.medium }, { value: 'low', label: CONF_LABEL.low }, { value: 'none', label: CONF_LABEL.none }])}
        {filterSelect('Source type', sourceType, setSourceType, [{ value: 'all', label: 'All' }, ...sourceTypes.map((t) => ({ value: t, label: t.toUpperCase() }))])}
        {filterSelect('Department', dept, setDept, [{ value: 'all', label: 'All' }, ...depts.map((d) => ({ value: d, label: d }))])}
        {filterSelect('Gate fired', gate, setGate, [{ value: 'all', label: 'All' }, ...gates.map((g) => ({ value: g, label: g }))])}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: C.subtle, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Search
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="EUC id or file" style={{
            fontFamily: mono, fontSize: 12, color: C.ink, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 8px', width: 140,
          }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: needsReviewOnly ? C.accent : C.subtle, cursor: 'pointer' }}>
          <input type="checkbox" checked={needsReviewOnly} onChange={(e) => setNeedsReviewOnly(e.target.checked)} />
          Needs review only
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: showCommitted ? C.accent : C.subtle, cursor: 'pointer' }}>
          <input type="checkbox" checked={showCommitted} onChange={(e) => setShowCommitted(e.target.checked)} />
          Show already-committed
        </label>
      </div>

      {/* Bulk action bar */}
      <div style={{ background: C.ink, borderRadius: 16, padding: 16, marginBottom: 16, color: '#e7e6f4' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>
            {selected.size} of {filtered.length} selected
            {selected.size > 0 && <span style={{ color: '#9a97c4', fontWeight: 500 }}> — {approvableCount} approvable as-is{needsOverrideCount > 0 ? `, ${needsOverrideCount} need an override` : ''}</span>}
          </div>
          <button onClick={selectAllFiltered} disabled={filtered.length === 0} style={{ fontSize: 11, fontWeight: 700, color: '#c9c2f5', background: 'transparent', border: '1px solid #453f7a', borderRadius: 8, padding: '5px 10px', cursor: filtered.length ? 'pointer' : 'default' }}>
            Select all filtered
          </button>
          {selected.size > 0 && (
            <button onClick={clearSelection} style={{ fontSize: 11, fontWeight: 700, color: '#c9c2f5', background: 'transparent', border: 'none', cursor: 'pointer' }}>
              Clear selection
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={() => setDecisionMode(decisionMode === 'approve' ? null : 'approve')} disabled={selected.size === 0}
            style={{ cursor: selected.size ? 'pointer' : 'default', opacity: selected.size ? 1 : 0.4, fontSize: 12, fontWeight: 700, color: decisionMode === 'approve' ? '#fff' : '#c9c2f5', background: decisionMode === 'approve' ? C.accent : 'transparent', border: `1px solid ${C.accent}`, borderRadius: 9, padding: '7px 14px' }}>
            Approve & Commit
          </button>
          <button onClick={() => setDecisionMode(decisionMode === 'override' ? null : 'override')} disabled={selected.size === 0}
            style={{ cursor: selected.size ? 'pointer' : 'default', opacity: selected.size ? 1 : 0.4, fontSize: 12, fontWeight: 700, color: decisionMode === 'override' ? '#fff' : '#c9c2f5', background: decisionMode === 'override' ? '#9a5b00' : 'transparent', border: '1px solid #b45309', borderRadius: 9, padding: '7px 14px' }}>
            Override & Commit
          </button>
        </div>

        {decisionMode && selectedRecords.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.1)', display: 'grid', gap: 10 }}>
            {decisionMode === 'override' && (
              <>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <select value={overrideTarget} onChange={(e) => setOverrideTarget(e.target.value as Outcome)} style={{
                    fontFamily: font.ui, fontSize: 12, color: '#e7e6f4', background: '#2a2650', border: '1px solid #453f7a', borderRadius: 8, padding: '7px 10px',
                  }}>
                    {OVERRIDE_TARGETS.map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
                  </select>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why (min. 10 characters — required, this is an audit trail entry for every selected EUC)" style={{
                    flex: 1, minWidth: 260, fontSize: 12, color: '#e7e6f4', background: '#2a2650', border: '1px solid #453f7a', borderRadius: 8, padding: '7px 10px', resize: 'vertical',
                  }} />
                </div>
                {needsOverrideCount === 0 && approvableCount > 0 && (
                  <div style={{ fontSize: 11, color: '#c9c2f5' }}>All {approvableCount} selected are already approvable — overriding will still replace their disposition with {OUTCOME_SHORT[overrideTarget]}.</div>
                )}
              </>
            )}
            {decisionMode === 'approve' && needsOverrideCount > 0 && (
              <div style={{ fontSize: 12, color: '#fde68a' }}>
                {needsOverrideCount} of {selectedRecords.length} selected {needsOverrideCount === 1 ? 'is' : 'are'} not in an approvable status (provisional/deferred/regenerate) — {needsOverrideCount === 1 ? 'it' : 'they'} will show up as a per-row error below unless resolved or overridden instead.
              </div>
            )}
            {showBox3Toggle && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#e7e6f4', cursor: 'pointer' }}>
                <input type="checkbox" checked={box3IncludePathC} onChange={(e) => setBox3IncludePathC(e.target.checked)} />
                Include Path C (platform-vs-on-prem placement needs justifying)
              </label>
            )}

            {/* Routing preview — spec §11's table, mirrored client-side for display only. */}
            {previewGroups.length > 0 && (
              <div style={{ background: '#2a2650', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#9a97c4', marginBottom: 6 }}>ROUTING PREVIEW</div>
                {previewGroups.map((g) => (
                  <div key={g.disposition} style={{ fontSize: 12, color: '#e7e6f4', marginBottom: 3 }}>
                    <b>{g.count}</b> × {OUTCOME_SHORT[g.disposition]} → {routeSummary(g.steps)}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={submit} disabled={!canSubmit || committing} style={{
                cursor: canSubmit && !committing ? 'pointer' : 'default', opacity: canSubmit && !committing ? 1 : 0.5,
                fontSize: 12, fontWeight: 700, color: '#fff', background: decisionMode === 'override' ? '#b45309' : C.accent, border: 'none', borderRadius: 9, padding: '8px 16px',
              }}>
                {committing ? 'Committing…' : `${decisionMode === 'override' ? 'Override' : 'Approve'} & commit ${selectedRecords.length}`}
              </button>
              <button onClick={() => { setDecisionMode(null); setReason(''); }} style={{ fontSize: 12, color: '#c9c2f5', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>

            {lastResult && lastResult.errors.length > 0 && (
              <div style={{ background: '#3a1f1f', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', color: '#fca5a5', marginBottom: 6 }}>{lastResult.errors.length} FAILED</div>
                {lastResult.errors.map((e) => (
                  <div key={e.euc_id} style={{ fontSize: 11, color: '#fecaca', marginBottom: 3 }}><b>{e.euc_id}</b> — {e.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rows */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '28px 1.6fr 0.9fr 1fr 0.8fr 0.9fr 0.9fr 1.3fr', gap: 8, padding: '9px 14px', background: C.soft, borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: C.label, textTransform: 'uppercase' }}>
          <span />
          <span>EUC</span>
          <span>Department</span>
          <span>Disposition</span>
          <span>Timing</span>
          <span>Confidence</span>
          <span>Status</span>
          <span>Committed</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: '28px 14px', textAlign: 'center', fontSize: 12.5, color: C.subtle }}>No EUCs match these filters.</div>
        ) : filtered.map((r) => {
          const conf = CONF_LEVEL(r.confidence);
          const s = STATUS_STYLE[r.status] || STATUS_STYLE.auto_confirmed;
          return (
            <div key={r.euc_id} style={{
              display: 'grid', gridTemplateColumns: '28px 1.6fr 0.9fr 1fr 0.8fr 0.9fr 0.9fr 1.3fr', gap: 8, padding: '10px 14px',
              borderBottom: `1px solid ${C.border}`, alignItems: 'center', background: selected.has(r.euc_id) ? '#f6f4fe' : 'transparent',
            }}>
              <input type="checkbox" checked={selected.has(r.euc_id)} onChange={() => toggleRow(r.euc_id)} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.file}</div>
                <div style={{ fontSize: 11, color: C.subtle }}>{r.euc_id}{r.source_type ? ` · ${r.source_type.toUpperCase()}` : ''}</div>
              </div>
              <div style={{ fontSize: 12, color: C.body }}>{r.dept || '—'}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>{OUTCOME_SHORT[r.disposition]}</div>
                {r.disposition_override && (
                  <div style={{ fontSize: 11, color: '#9a5b00' }}>overridden from {OUTCOME_SHORT[r.engine_disposition || r.disposition]}</div>
                )}
              </div>
              <div style={{ fontSize: 12, color: C.subtle }}>{TIMING_LABEL[r.timing]}</div>
              <div style={{ fontSize: 12, color: C.subtle }}>{r.confidence == null ? '—' : `${Math.round(r.confidence * 100)}%`} <span style={{ color: C.label }}>({conf})</span></div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', padding: '3px 8px', borderRadius: 999, background: s.bg, color: s.fg }}>{s.label}</span>
              </div>
              <div style={{ fontSize: 11, color: C.subtle }}>
                {r.committed ? (
                  <span title={r.committed_by ? `${r.committed_by.name || r.committed_by.email || 'user'} · ${r.committed_at || ''}` : undefined}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: C.accent }}>✓ {r.commit_decision === 'overridden' ? 'Overridden' : 'Approved'}</span>
                    <div style={{ marginTop: 2 }}>{routeSummary(r.routing)}</div>
                  </span>
                ) : '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
